import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from "chart.js";
import { useEffect,useRef } from "react";
import { Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TerminationsChart = ({ series, categories }) => {
  const chartContainerRef = useRef(null);

  useEffect(() => {
    if (chartContainerRef.current) {
      chartContainerRef.current.scrollLeft = chartContainerRef.current.scrollWidth;
    }
  }, [categories]); // Scrolls to right when categories change

  // Ensure every line gets a distinct, high-contrast color.
  const COLORS_BY_NAME = {
    // Common labels (depending on API naming)
    Resign: "#f59e0b",
    Resigned: "#f59e0b",
    Terminate: "#D3001A",
    Terminated: "#D3001A",
    "Non-Renewal": "#1AA8FB",
    "Non Renewal": "#1AA8FB",
    Contract: "#1AA8FB",
    Absconding: "#9368E9",
  };
  const FALLBACK_COLORS = ["#D3001A", "#1AA8FB", "#55CE63", "#f59e0b", "#9368E9", "#02a8b5"];

  // Prepare data for Chart.js
  const data = {
    labels: categories,
    datasets: series.map((serie, index) => {
      const lineColor =
        COLORS_BY_NAME[serie.name] ||
        FALLBACK_COLORS[index % FALLBACK_COLORS.length];

      return {
        label: serie.name,
        data: serie.data,
        borderColor: lineColor,
        backgroundColor: lineColor,
        pointBackgroundColor: lineColor,
        pointBorderColor: "#ffffff",
        tension: 0.4, // for smooth curves
        borderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        enabled: true,
      },
      datalabels: {
        align: 'top',

        display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
        color: "#000",
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (value) => (value === 0 ? "" : value),
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of Employees",
        },
        max: (context) => {
          const allValues = context.chart.data.datasets.flatMap(dataset => dataset.data);
          const maxValue = Math.max(...allValues);
          
          // Find the step size by analyzing the data
          const sortedValues = [...new Set(allValues)].sort((a, b) => a - b);
          let stepSize = 1;
          
          if (sortedValues.length >= 2) {
            // Calculate differences between consecutive values
            const differences = [];
            for (let i = 1; i < sortedValues.length; i++) {
              differences.push(sortedValues[i] - sortedValues[i-1]);
            }
            // Use the most common difference as step size
            stepSize = Math.min(...differences);
          }
          
          // Store the step size in the chart instance for use in ticks
          context.chart.stepSize = stepSize;
          
          // Add two more steps above the maximum
          return maxValue + (stepSize * 2);
        },
        ticks: {
          callback: function(value) {
            // Use the step size stored in the chart instance
            return value % this.chart.stepSize === 0 ? value : '';
          }
        }
      },
    },
  };

  return (
    <div
      ref={chartContainerRef}
      style={{
        marginBottom: "0",
        overflowX: "auto",
        overflowY: "hidden", 
        paddingTop: "10px",
      }}
    >
      <div style={{ width: `${categories.length * 50}px`, minWidth: "600px", height: "290px" }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default TerminationsChart;
